import Glide from '@glidejs/glide'
import config from './config/config'
import { useStore } from 'vuex'
import { onMounted, ref } from 'vue'
import ProductCard from 'components/ProductCard/ProductCard'
import MaterialCard from 'components/MaterialCard/MaterialCard'
import SpotCardGrid from 'components/SpotCardGrid/SpotCardGrid'

const unifyCardHeight = function (root) {
    root.value.querySelectorAll('.glide__slide').forEach((slide) => {
        slide.style.height = `auto`
    })
}

export default {
    name: 'ProductsCardsSlider',
    components: { ProductCard, MaterialCard, SpotCardGrid },
    props: {
        glideBehaviour: {
            type: String,
            required: false,
            default: 'slider'
        },
        results: {
            type: Array,
            required: true
        },
        spotCardData: {
            type: Object,
            required: false
        },
        spotCardIndex: {
            type: Number,
            required: false
        },
        type: {
            type: String,
            required: false,
            default: 'products'
        },
        link: {
            type: Object,
            required: false
        },
        sliderId: {
            type: String,
            required: false
        },
        showAddCta: {
            type: Boolean,
            default: false
        },
        sizingFunction: {
            type: Function,
            required: false
        }
    },
    setup(props) {
        const root = ref(null)
        const componentRootEl = ref(null)
        const prevBtn = ref(null)
        const nextBtn = ref(null)

        const store = useStore()
        const glidePosition = ref(0)

        const displayProperties = {
            products: { card: 'ProductCard' },
            materials: { card: 'MaterialCard' }
        }[props.type] ?? { card: 'ProductCard' }

        const getLabel = (labelKey) =>
            store.getters['labels/getLabel']({
                labelCollection: `productsCardsSlider-${props.sliderId}`,
                labelKey
            })

        const configFunction = function () {
            const glideBehaviour = props.glideBehaviour
            return config({
                glideBehaviour,
                startAt: glidePosition.value,
                sizingFunction: props.sizingFunction
            })
        }

        const truncateSummary = function (summary) {
            return summary.length > 120
                ? summary.substring(0, 117) + '...'
                : summary
        }

        const shouldRenderSpotCard = function (index) {
            return props.spotCardIndex - 1 === index
        }

        onMounted(() => {
            unifyCardHeight(root)

            const sliderDOM = document.querySelector(`#${props.sliderId}`)
            if (!sliderDOM) return

            const glide = new Glide(`#${props.sliderId}`, configFunction())

            const syncArrowAria = () => {
                const prev = prevBtn.value
                const next = nextBtn.value
                if (!prev || !next) return

                const prevDisabled = prev.classList.contains(
                    'glide__arrow--disabled'
                )
                const nextDisabled = next.classList.contains(
                    'glide__arrow--disabled'
                )

                prev.setAttribute('aria-disabled', String(prevDisabled))
                next.setAttribute('aria-disabled', String(nextDisabled))

                prev.tabIndex = prevDisabled ? -1 : 0
                next.tabIndex = nextDisabled ? -1 : 0
            }

            glide.on('resize', function () {
                glide.update(configFunction())
                syncArrowAria()
            })

            glide.on('move', function () {
                glidePosition.value = glide.index
            })

            glide.on(['mount.after', 'run.after'], syncArrowAria)

            glide.mount()
            syncArrowAria()

            document
                .querySelector('.glide')
                ?.addEventListener('keydown', function (event) {
                    if (event.keyCode == 9) {
                        var card = event.target?.parentNode
                        if (card == null) return
                        var nextCard = event.shiftKey
                            ? card.previousSibling
                            : card.nextSibling
                        var nextIndex =
                            nextCard?.attributes['data-index']?.value
                        if (nextIndex) glide.go('=' + nextIndex)
                    }
                })
        })

        return {
            displayProperties,
            componentRootEl,
            getLabel,
            root,
            link: props.link,
            truncateSummary,
            shouldRenderSpotCard,
            prevBtn,
            nextBtn
        }
    }
}
